import { createAppConfig, AppConfig, AppTier, Environment } from './appConfig';

// Platform detection utilities
export const getPlatform = (): 'mobile' | 'web' => {
  try {
    // React Native commonly exposes navigator.product = 'ReactNative'.
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
      return 'mobile';
    }

    // Browser runtimes expose document.
    if (typeof document !== 'undefined') {
      return 'web';
    }
  } catch (e) {
    // Ignore error for React Native environment
  }
  return 'mobile';
};

// Build-time configuration injection
// These would typically be set via environment variables or build scripts
declare global {
  var __APP_TIER__: AppTier | undefined;
  var __APP_VERSION__: string | undefined;
  var __BUILD_NUMBER__: string | undefined;
  var __ENVIRONMENT__: Environment | undefined;
  var __API_BASE_URL__: string | undefined;
}

// Configuration manager class
export class ConfigManager {
  private static instance: ConfigManager;
  private config: AppConfig;
  private static debugPremiumOverride: boolean | null = null; // null = use default, true = force premium, false = force standard
  private static navButtonColor: string = '#10b981'; // Default: teal green
  private static navButtonColorInactive: string = '#f1f3f4'; // Default: light gray
  private static appBackgroundColor: string = '#3b82f6'; // Default: primary blue
  private static headerBackgroundColor: string = '#3b82f6'; // Default: primary blue
  private static headerWidthMax: number = 850; // Default max width for header content (px)
  private static footerWidthMax: number = 500; // Default max width for footer content (px)
  private static singleCardMaxWidth: number = 750; // Default max width for single content cards (px)
  private static cardMargin: number = 16; // Default margin around content cards (px)
  private static twoCardMaxWidth: number = 1000; // Default max width for two-column card layouts (px)
  private static skinType: string = 'default'; // Skin/branding type identifier
  private static logoImageId: string = '3'; // ImageId of the Base64Image record to use as logo
  private static mobileHeaderTopPadding: number = 5; // Extra top padding (px) added to page headers on mobile to clear the status bar
  private static logoImageListeners: Array<() => void> = []; // Listeners notified when a logo image is saved

  /** Notify all registered listeners that a new logo image was stored. */
  public static notifyLogoImageUpdated(): void {
    ConfigManager.logoImageListeners.forEach(fn => {
      try { fn(); } catch (_) { /* ignore */ }
    });
  }

  /** Register a listener; returns an unsubscribe function. */
  public static addLogoImageListener(fn: () => void): () => void {
    ConfigManager.logoImageListeners.push(fn);
    return () => {
      ConfigManager.logoImageListeners = ConfigManager.logoImageListeners.filter(f => f !== fn);
    };
  }

  private constructor() {
    // Determine configuration from build-time variables or defaults
    const tier: AppTier = globalThis.__APP_TIER__ || 'standard';
    const environment: Environment = globalThis.__ENVIRONMENT__ || 'development';
    const version = globalThis.__APP_VERSION__ || '1.0.0';
    const buildNumber = globalThis.__BUILD_NUMBER__ || '1';

    // Create configuration with overrides
    this.config = createAppConfig(tier, environment, {
      appVersion: version,
      buildNumber: buildNumber,
    });

    // Allow an injected API base URL to override the environment map.
    // Web:    set VITE_API_BASE_URL at build time → injected as __API_BASE_URL__ by vite.config.ts
    // Mobile: set API_BASE_URL in the .env.* file  → written to globalThis by src/environment.ts
    const injectedBaseUrl = globalThis.__API_BASE_URL__;
    if (injectedBaseUrl) {
      this.config.api.baseUrl = injectedBaseUrl;
    }

    // Validate configuration
    this.validateConfiguration();
  }

  public static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  public getConfig(): AppConfig {
    return this.config;
  }

  public updateConfig(overrides: Partial<AppConfig>): void {
    this.config = { ...this.config, ...overrides };
    this.validateConfiguration();
  }

  public hasFeature(feature: keyof AppConfig['features']): boolean {
    return Boolean(this.config.features[feature]);
  }

  public isPremium(): boolean {
    // Check for debug override first
    if (ConfigManager.debugPremiumOverride !== null) {
      return ConfigManager.debugPremiumOverride;
    }
    
    return this.config.tier === 'premium';
  }

  public isStandard(): boolean {
    return this.config.tier === 'standard';
  }

  public isDevelopment(): boolean {
    return this.config.environment === 'development';
  }

  public isProduction(): boolean {
    return this.config.environment === 'production';
  }

  public getDevelopmentDomains(): string[] {
    if (this.isPremium()) {
      return ['communication', 'fineMotor', 'grossMotor', 'cognitive', 'socialEmotional'];
    } else {
      return ['communication', 'motor', 'cognitive', 'socialEmotional'];
    }
  }

  public getMaxChildren(): number {
    // Unlimited profiles for both standard and premium versions
    return Infinity;
  }

  public getSyncInterval(): number {
    return this.config.api.syncInterval;
  }

  public shouldEncryptStorage(): boolean {
    return this.config.storage.enableEncryption;
  }

  public getMaxCacheSize(): number {
    return this.config.storage.maxCacheSize;
  }

  public shouldEnableAnalytics(): boolean {
    return this.config.analytics.enabled;
  }

  public shouldEnableLogging(): boolean {
    return this.config.debug.enableLogging;
  }

  public getLogLevel(): AppConfig['debug']['logLevel'] {
    return this.config.debug.logLevel;
  }

  public getApiBaseUrl(): string {
    return this.config.api.baseUrl;
  }

  public getApiTimeout(): number {
    return this.config.api.timeout;
  }

  public getRetryAttempts(): number {
    return this.config.api.retryAttempts;
  }

  public getDevInitWindow(): number {
    return this.config.development.devInitWindow;
  }

  private validateConfiguration(): void {
    // Basic validation
    if (!this.config.appName) {
      throw new Error('App name is required');
    }

    if (!this.config.appVersion) {
      throw new Error('App version is required');
    }

    // Feature consistency validation
    if (this.isStandard() && this.hasFeature('separateMotorDomains')) {
      console.warn('Warning: separateMotorDomains feature is not available in standard tier');
      this.config.features.separateMotorDomains = false;
    }

    if (this.isStandard() && this.hasFeature('percentileTracking')) {
      console.warn('Warning: percentileTracking feature is not available in standard tier');
      this.config.features.percentileTracking = false;
    }

    // API validation for production
    if (this.isProduction() && !this.config.api.baseUrl) {
      throw new Error('API base URL is required for production environment');
    }
  }
  
  // Debug methods for premium mode override
  public static setDebugPremiumOverride(override: boolean | null): void {
    ConfigManager.debugPremiumOverride = override;
    console.log('🏆 Debug premium override set:', override === null ? 'default' : (override ? 'premium' : 'standard'));
  }
  
  public static getDebugPremiumOverride(): boolean | null {
    return ConfigManager.debugPremiumOverride;
  }

  public getNavButtonColor(): string {
    return ConfigManager.navButtonColor;
  }

  public setNavButtonColor(color: string): void {
    ConfigManager.navButtonColor = color;
  }

  public getNavButtonColorInactive(): string {
    return ConfigManager.navButtonColorInactive;
  }

  public setNavButtonColorInactive(color: string): void {
    ConfigManager.navButtonColorInactive = color;
  }

  public getAppBackgroundColor(): string {
    return ConfigManager.appBackgroundColor;
  }

  public setAppBackgroundColor(color: string): void {
    ConfigManager.appBackgroundColor = color;
  }

  public getHeaderBackgroundColor(): string {
    return ConfigManager.headerBackgroundColor;
  }

  public setHeaderBackgroundColor(color: string): void {
    ConfigManager.headerBackgroundColor = color;
  }

  public getHeaderWidthMax(): number {
    return ConfigManager.headerWidthMax;
  }

  public setHeaderWidthMax(width: number): void {
    ConfigManager.headerWidthMax = width;
  }

  public getFooterWidthMax(): number {
    return ConfigManager.footerWidthMax;
  }

  public setFooterWidthMax(width: number): void {
    ConfigManager.footerWidthMax = width;
  }

  public getSingleCardMaxWidth(): number {
    return ConfigManager.singleCardMaxWidth;
  }

  public setSingleCardMaxWidth(width: number): void {
    ConfigManager.singleCardMaxWidth = width;
  }

  public getCardMargin(): number {
    return ConfigManager.cardMargin;
  }

  public setCardMargin(margin: number): void {
    ConfigManager.cardMargin = margin;
  }

  public getTwoCardMaxWidth(): number {
    return ConfigManager.twoCardMaxWidth;
  }

  public setTwoCardMaxWidth(width: number): void {
    ConfigManager.twoCardMaxWidth = width;
  }

  public getSkinType(): string {
    return ConfigManager.skinType;
  }

  public setSkinType(type: string): void {
    ConfigManager.skinType = type;
  }

  public getLogoImageId(): string {
    return ConfigManager.logoImageId;
  }

  public setLogoImageId(imageId: string): void {
    ConfigManager.logoImageId = imageId;
  }

  public getMobileHeaderTopPadding(): number {
    return getPlatform() === 'mobile' ? ConfigManager.mobileHeaderTopPadding : 0;
  }

  public setMobileHeaderTopPadding(value: number): void {
    ConfigManager.mobileHeaderTopPadding = value;
  }
}

// Global configuration instance
export const appConfig = ConfigManager.getInstance();

// Feature checking functions for convenience
export const hasFeature = (feature: keyof AppConfig['features']): boolean => {
  return appConfig.hasFeature(feature);
};

export const isPremium = (): boolean => {
  return appConfig.isPremium();
};

export const getNavButtonColor = (): string => {
  return appConfig.getNavButtonColor();
};

export const setNavButtonColor = (color: string): void => {
  appConfig.setNavButtonColor(color);
};

export const getNavButtonColorInactive = (): string => {
  return appConfig.getNavButtonColorInactive();
};

export const setNavButtonColorInactive = (color: string): void => {
  appConfig.setNavButtonColorInactive(color);
};

export const getAppBackgroundColor = (): string => {
  return appConfig.getAppBackgroundColor();
};

export const setAppBackgroundColor = (color: string): void => {
  appConfig.setAppBackgroundColor(color);
};

export const getHeaderBackgroundColor = (): string => {
  return appConfig.getHeaderBackgroundColor();
};

export const setHeaderBackgroundColor = (color: string): void => {
  appConfig.setHeaderBackgroundColor(color);
};

export const getHeaderWidthMax = (): number => {
  return appConfig.getHeaderWidthMax();
};

export const setHeaderWidthMax = (width: number): void => {
  appConfig.setHeaderWidthMax(width);
};

export const getFooterWidthMax = (): number => {
  return appConfig.getFooterWidthMax();
};

export const setFooterWidthMax = (width: number): void => {
  appConfig.setFooterWidthMax(width);
};

export const getSingleCardMaxWidth = (): number => {
  return appConfig.getSingleCardMaxWidth();
};

export const setSingleCardMaxWidth = (width: number): void => {
  appConfig.setSingleCardMaxWidth(width);
};

export const getCardMargin = (): number => {
  return appConfig.getCardMargin();
};

export const setCardMargin = (margin: number): void => {
  appConfig.setCardMargin(margin);
};

export const getMobileHeaderTopPadding = (): number => {
  return appConfig.getMobileHeaderTopPadding();
};

export const setMobileHeaderTopPadding = (value: number): void => {
  appConfig.setMobileHeaderTopPadding(value);
};

export const getTwoCardMaxWidth = (): number => {
  return appConfig.getTwoCardMaxWidth();
};

export const setTwoCardMaxWidth = (width: number): void => {
  appConfig.setTwoCardMaxWidth(width);
};

export const getSkinType = (): string => {
  return appConfig.getSkinType();
};

export const setSkinType = (type: string): void => {
  appConfig.setSkinType(type);
};

export const getLogoImageId = (): string => {
  return appConfig.getLogoImageId();
};

export const setLogoImageId = (imageId: string): void => {
  appConfig.setLogoImageId(imageId);
};

export const isStandard = (): boolean => {
  return appConfig.isStandard();
};

export const isDevelopment = (): boolean => {
  return appConfig.isDevelopment();
};

export const isProduction = (): boolean => {
  return appConfig.isProduction();
};

// Platform and tier-specific utilities
export const getDevelopmentDomains = (): string[] => {
  return appConfig.getDevelopmentDomains();
};

export const getMaxChildren = (): number => {
  return appConfig.getMaxChildren();
};

export const shouldSeparateMotorDomains = (): boolean => {
  return hasFeature('separateMotorDomains');
};

export const shouldTrackPercentiles = (): boolean => {
  return hasFeature('percentileTracking');
};

export const shouldEnableCloudBackup = (): boolean => {
  return hasFeature('cloudBackup');
};

export const shouldEnableFamilySharing = (): boolean => {
  return hasFeature('familySharing');
};

export const shouldEnableExportReports = (): boolean => {
  return hasFeature('exportReports');
};

export const shouldEnableProviderSync = (): boolean => {
  return hasFeature('healthcareProviderSync') || hasFeature('schoolProviderSync');
};

// Configuration display helpers
export const getAppDisplayName = (): string => {
  const config = appConfig.getConfig();
  const tierSuffix = isPremium() ? ' Premium' : '';
  return `${config.appName}${tierSuffix}`;
};

export const getAppVersion = (): string => {
  const config = appConfig.getConfig();
  return `${config.appVersion} (${config.buildNumber})`;
};

export const getEnvironmentInfo = (): string => {
  const config = appConfig.getConfig();
  const platform = getPlatform();
  return `${config.environment}/${platform}`;
};

// Development utilities
export const debugConfig = (): void => {
  if (isDevelopment()) {
    console.log('App Configuration:', appConfig.getConfig());
    console.log('Platform:', getPlatform());
    console.log('Features enabled:', Object.entries(appConfig.getConfig().features)
      .filter(([_, enabled]) => enabled)
      .map(([feature]) => feature)
    );
  }
};

// Export the configuration instance for direct access
export default appConfig;